/**
 * Theming primitives shared across all ModernATC apps.
 *
 * Preference (what the user picked) ∈ dark | light | auto. The applied
 * attribute on <html> is always the RESOLVED concrete theme (dark | light);
 * `auto` is resolved against the OS `prefers-color-scheme` at runtime.
 *
 * Persistence: a `theme` cookie (so the server can render the right theme and
 * so it survives across the session) plus localStorage (belt-and-suspenders).
 * For signed-in users the durable store is `profiles.theme`, mirrored to the
 * cookie on the app shell so this client code stays simple.
 */

export type ThemePreference = "dark" | "light" | "auto";
export type ResolvedTheme = "dark" | "light";

export const THEME_COOKIE = "theme";
export const THEME_STORAGE_KEY = "theme";
export const DEFAULT_THEME: ThemePreference = "dark";
export const THEME_PREFERENCES: readonly ThemePreference[] = [
  "dark",
  "light",
  "auto",
] as const;

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "dark" || value === "light" || value === "auto";
}

/** Resolve a preference to the concrete theme to apply (auto → system). */
export function resolveTheme(
  pref: ThemePreference,
  systemPrefersLight: boolean,
): ResolvedTheme {
  if (pref === "light") return "light";
  if (pref === "dark") return "dark";
  return systemPrefersLight ? "light" : "dark";
}

/**
 * The blocking, inline no-flash script. Rendered into the document head so it
 * runs before first paint: it reads the saved preference (cookie → localStorage
 * → default dark), resolves `auto` against the OS, and sets `data-theme` +
 * `color-scheme` on <html>. It also keeps `auto` reactive to OS changes.
 */
export const themeScriptSource = `(function(){try{
var d=document.documentElement;
function read(){var m=document.cookie.match(/(?:^|; )theme=([^;]+)/);var p=m?decodeURIComponent(m[1]):null;if(p!=='dark'&&p!=='light'&&p!=='auto'){try{p=localStorage.getItem('theme')}catch(e){p=null}}if(p!=='dark'&&p!=='light'&&p!=='auto')p='dark';return p}
function resolve(p){return p==='light'?'light':p==='dark'?'dark':(window.matchMedia('(prefers-color-scheme: light)').matches?'light':'dark')}
function apply(){var r=resolve(read());d.setAttribute('data-theme',r);d.style.colorScheme=r}
apply();
try{window.matchMedia('(prefers-color-scheme: light)').addEventListener('change',function(){if(read()==='auto')apply()})}catch(e){}
}catch(e){}})();`;

/** Read the saved preference on the client (cookie first, then localStorage). */
export function readStoredPreference(): ThemePreference {
  if (typeof document === "undefined") return DEFAULT_THEME;
  const m = document.cookie.match(/(?:^|; )theme=([^;]+)/);
  const cookieVal = m ? decodeURIComponent(m[1] ?? "") : null;
  if (isThemePreference(cookieVal)) return cookieVal;
  try {
    const ls = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemePreference(ls)) return ls;
  } catch {
    /* storage blocked */
  }
  return DEFAULT_THEME;
}

/** Persist a preference (cookie + localStorage) and apply it to <html> now. */
export function applyThemePreference(pref: ThemePreference): void {
  if (typeof document === "undefined") return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* storage blocked */
  }
  // 1 year, lax, root path — readable by the server for SSR.
  document.cookie = `${THEME_COOKIE}=${pref}; path=/; max-age=31536000; samesite=lax`;
  const resolved = resolveTheme(
    pref,
    window.matchMedia("(prefers-color-scheme: light)").matches,
  );
  const el = document.documentElement;
  el.setAttribute("data-theme", resolved);
  el.style.colorScheme = resolved;
}
